import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { allowanceTotals, holidayYearOf } from '../../utils/leaveDays';
import { ComplianceCard } from './HomeCards';

// ─── My Space on the home page ───────────────────────────────────────────────
//
// Most people here do not run a SOW, so a home page that opened on milestones
// and invoicing said nothing to them. What everybody wants on the way in is
// their own admin: how much holiday is left to book, when they are next off,
// and whether finance is waiting on a receipt from them. Each tile is one
// figure and one line of context, and opens the page where it can be acted on.

function fmtDay(d) {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function daysUntil(d, today) {
  return Math.round((Date.parse(d) - Date.parse(today)) / 86400000);
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function Tile({ label, onClick, tone = 'default', children }) {
  const border = tone === 'alert' ? 'border-red-200 bg-red-50' : tone === 'good' ? 'border-emerald-200 bg-white' : 'border-gray-200 bg-white';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left border rounded-md px-3 py-2.5 hover:border-gold transition-colors min-w-0 ${border}`}
    >
      <div className={`text-[8px] font-bold uppercase tracking-wide mb-1 ${tone === 'alert' ? 'text-red-600' : 'text-gray-500'}`}>{label}</div>
      {children}
    </button>
  );
}

export default function MySpaceStrip({ user, onNavigate, compact = false }) {
  const [allowances, setAllowances] = useState(null);
  const [requests, setRequests]     = useState([]);
  const [bankHols, setBankHols]     = useState([]);
  // Company card transactions with no receipt against them. Nobody but the
  // cardholder can fix this, and it costs real money: with no receipt the VAT
  // cannot be reclaimed, so finance end up chasing it by hand. Counted on what
  // is still theirs to upload: one they have told finance there is no receipt
  // for is not. Null for somebody who holds no card, so the tile stays away.
  const [cardSpend, setCardSpend]   = useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    if (!user?.id) return;
    api.get(`people/${user.id}/leave-allowances`, { year: holidayYearOf(today) })
      .then(a => setAllowances(Array.isArray(a) ? a : []))
      .catch(() => setAllowances([]));
    api.get(`people/${user.id}/leave-requests`)
      .then(r => setRequests(Array.isArray(r) ? r : []))
      .catch(() => setRequests([]));
    api.get('cot/transactions', { mine: '1', missing_receipt: '1' })
      .then(d => setCardSpend(d?.cards?.length ? d.summary || { awaiting_upload: 0 } : null))
      .catch(() => setCardSpend(null));
  }, [user?.id, today]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  useEffect(() => {
    const to = new Date(Date.now() + 366 * 86400000).toISOString().slice(0, 10);
    api.get('bank-holidays', { from: today, to })
      .then(rows => setBankHols(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [today]);

  // ── Holiday left to book ──
  // The same figure as Remaining on My Leave: entitlement less what is booked,
  // across the capped allowances (sickness is a count, not an allowance).
  const totals = allowances ? allowanceTotals(allowances) : null;
  // Carried-over days are lost if not taken by their date, which is the one
  // thing about a balance worth a warning on the home page.
  const carry = (allowances || []).find(a => a.leave_type === 'carry_over' && a.expires_on
    && (a.total_days || 0) - (a.booked_days || 0) > 0 && a.expires_on >= today);
  const carryLeft = carry ? (carry.total_days || 0) - (carry.booked_days || 0) : 0;

  // ── Next time off ──
  // Your own leave that has not finished, approved or still waiting, and the
  // next bank holiday, whichever comes first. Sickness is not "time off" you
  // look forward to, so it is left out.
  const nextLeave = requests
    .filter(r => (r.status === 'approved' || r.status === 'pending') && r.leave_type !== 'sick' && r.end_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date))[0] || null;
  const nextBankHol = bankHols.find(b => b.holiday_date >= today) || null;
  const leaveFirst = nextLeave && (!nextBankHol || nextLeave.start_date <= nextBankHol.holiday_date);

  return (
    <div className={compact ? '' : 'mb-6'}>
      {!compact && <h3 className="text-[11px] font-bold text-navy mb-2">My Space</h3>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">

        <Tile label="Holiday left to book" onClick={() => onNavigate && onNavigate('my-leave')}>
          {totals == null ? (
            <div className="h-6 w-16 bg-gray-100 rounded animate-pulse" />
          ) : totals.entitlement === 0 ? (
            <div className="text-[10px] text-gray-500">No allowance set for {holidayYearOf(today)}. HR sets this on your record.</div>
          ) : (
            <>
              <div className={`text-xl font-extrabold leading-none ${totals.remaining < 0 ? 'text-red-600' : 'text-navy'}`}>
                {totals.remaining} <span className="text-[10px] font-semibold text-gray-500">of {totals.entitlement} days</span>
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                {carryLeft > 0
                  ? <span className="text-amber-700 font-semibold">{plural(carryLeft, 'carried-over day')} to take by {fmtDay(carry.expires_on)}</span>
                  : `${totals.booked} booked this holiday year (to end of February)`}
              </div>
            </>
          )}
        </Tile>

        <Tile label="Next time off" onClick={() => onNavigate && onNavigate('my-leave')}>
          {leaveFirst ? (
            <>
              <div className="text-sm font-bold text-navy leading-tight">
                {nextLeave.start_date <= today ? 'Off now' : fmtDay(nextLeave.start_date)}
                {nextLeave.end_date !== nextLeave.start_date && <span className="font-semibold text-gray-500"> to {fmtDay(nextLeave.end_date)}</span>}
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                {plural(nextLeave.days || 0, 'day')}
                {nextLeave.start_date > today && ` · in ${plural(daysUntil(nextLeave.start_date, today), 'day')}`}
                {nextLeave.status === 'pending' && <span className="ml-1 px-1 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold">Awaiting approval</span>}
              </div>
            </>
          ) : nextBankHol ? (
            <>
              <div className="text-sm font-bold text-navy leading-tight">{fmtDay(nextBankHol.holiday_date)}</div>
              <div className="text-[9px] text-gray-500 mt-1">
                {nextBankHol.name} · in {plural(daysUntil(nextBankHol.holiday_date, today), 'day')}. Nothing booked before then.
              </div>
            </>
          ) : (
            <div className="text-[10px] text-gray-500">Nothing booked. Request leave on My Leave &amp; Holidays.</div>
          )}
        </Tile>

        {cardSpend && (
          cardSpend.awaiting_upload > 0 ? (
            <Tile label="Company card receipts" tone="alert" onClick={() => onNavigate && onNavigate('my-card-spend')}>
              <div className="text-xl font-extrabold leading-none text-red-600">
                {cardSpend.awaiting_upload} <span className="text-[10px] font-semibold">to upload</span>
              </div>
              <div className="text-[9px] text-red-700 mt-1">
                About £{Number(cardSpend.awaiting_upload_vat_at_risk || 0).toFixed(2)} of VAT we cannot reclaim until they are in.
                {/* Here, not the Capital on Tap app: a receipt added there has to be
                    downloaded and re-uploaded by finance by hand. */}
                {' '}Upload here: it goes straight to Sage.
              </div>
            </Tile>
          ) : (
            <Tile label="Company card receipts" tone="good" onClick={() => onNavigate && onNavigate('my-card-spend')}>
              <div className="text-sm font-bold text-emerald-700 leading-tight">All in</div>
              <div className="text-[9px] text-gray-500 mt-1">Every card transaction has a receipt or a reason.</div>
            </Tile>
          )
        )}

        {/* Clearance, site pass, RDN and certifications: only for somebody
            who holds any of them. */}
        <ComplianceCard />

      </div>
    </div>
  );
}
